/**
 * @fileoverview Direct reports query hook.
 * @description Reads whether anyone reports directly to a user (the directory's Reporting Manager
 * filter, one row per status rather than every report), so the Deactivate dialog only says that
 * reports move up when there is someone to move, and can hold the confirm button until it knows.
 * People who are themselves already deactivated do not count: Deactivate leaves them where they
 * are. A lifecycle command marks it stale (without refetching what a closing dialog shows).
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useQuery } from '@tanstack/react-query'

import { USERS_QUERY_KEYS } from '@/features/users/constants/users-query-keys.constants'
import { hasMovableDirectReports } from '@/features/users/services/users.service'

import type { ApiError } from '@/shared/types/http.types'

/**
 * @description Asks whether anyone who would move reports directly to a user.
 * @param {string} userId - The user.
 * @returns {import('@tanstack/react-query').UseQueryResult<boolean, ApiError>} The query result:
 * `data` is true when someone would move, false when nobody would, and absent until it has loaded.
 */
export function useDirectReports(userId: string) {
  return useQuery<boolean, ApiError>({
    queryKey: [...USERS_QUERY_KEYS.directReports, userId],
    queryFn: () => hasMovableDirectReports(userId),
  })
}
